// Broker Channel Batch (WS7b) — the broker Certifications tab. A checklist
// over the 3-item broker certification catalog (Pending → Recorded →
// Verified, Brokers.Qualify mutations) with a per-item slide-in drawer:
// evidence form (serialized to evidenceJson), Record / Verify / Reopen
// actions, and a reports section that deep-links the Report Generator
// (?templateId&brokerId) and files a generated report into the broker's DMS.
// Stage-gate certifications surface in the stage stepper's readiness
// checklist (StageAdvancePanel), not on this tab.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, ExternalLink, FileCheck2 } from "lucide-react";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { Modal } from "@/components/ui/Modal";
import {
  Badge,
  Button,
  FormField,
  Input,
  SubmitButton,
  useSubmittingMutation,
  useToast,
} from "@/design-system/components/ui";
import { buttonVariants } from "@/design-system/components/ui/Button";
import { Textarea } from "@/components/ui/Select";
import { extractErrorMessage } from "@/api/client";
import { reportSubmissionsApi } from "@/api/reportTemplates";
import { useAssignableUsers } from "@/hooks/useAssignableUsers";
import { formatCalendarDate, isoDateInTz, useDateFormatter } from "@/utils/datetime";
import { useCreateAppointment } from "@/pages/calendar/hooks/useAppointments";
import { wallClockToUtcIso } from "@/pages/calendar/lib/tz";
import {
  brokersApi,
  type BrokerCertificationDto,
  type BrokerCertificationStatus,
} from "@/api/brokers";

const STATUS_BADGE: Record<
  BrokerCertificationStatus,
  "archived" | "review" | "finalized" | "gold"
> = {
  Pending: "archived",
  Recorded: "review",
  Verified: "finalized",
  // A waiver is a notable administrative override — the gold chip is distinct
  // from the green "Verified", gray "Pending", and amber "Recorded".
  Waived: "gold",
};

interface EvidenceField {
  key: string;
  label: string;
  type?: "text" | "number" | "date";
  /** Required to RECORD the certification (blocks save when blank). */
  required?: boolean;
  /** Alternate keys accepted when prefilling from stored evidenceJson
   *  (application-import seeds use other casings/shapes). */
  aliases?: string[];
}

// Keys match what BrokerCertificationService.SeedFromApplicationAsync stores
// for BRK-INS; BRK-LIC seeds an ARRAY of PascalCase license rows, handled by
// the alias + first-element logic in parseEvidence below. Every catalogued
// field is required evidence — recording is blocked until they're filled.
const EVIDENCE_FIELDS: Record<string, EvidenceField[]> = {
  "BRK-INS": [
    { key: "provider", label: "Provider", required: true },
    { key: "policyNumber", label: "Policy number", required: true },
    { key: "coverageAmount", label: "Coverage (USD)", type: "number", required: true },
    { key: "expirationDate", label: "Expiration date", type: "date", required: true },
  ],
  "BRK-LIC": [
    {
      key: "issuingOrganization",
      label: "Issuing organization",
      required: true,
      aliases: ["licenseTypeIssuingOrganization"],
    },
    { key: "licenseNumber", label: "License number", required: true },
    { key: "state", label: "State", required: true },
    { key: "expirationDate", label: "Expiration date", type: "date", required: true },
  ],
  "BRK-TRN": [
    { key: "completionDate", label: "Completion date", type: "date", required: true },
  ],
};

const GENERIC_EVIDENCE: EvidenceField[] = [{ key: "details", label: "Evidence details" }];

export function evidenceFieldsFor(code: string): EvidenceField[] {
  return EVIDENCE_FIELDS[code] ?? GENERIC_EVIDENCE;
}

/** Defensive prefill: stored evidence may be an object (any key casing) or
 *  an array of rows (BRK-LIC application seed) — take the first row. */
export function parseEvidence(
  evidenceJson: string | null | undefined,
  fields: EvidenceField[],
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!evidenceJson) return out;
  let parsed: unknown;
  try {
    parsed = JSON.parse(evidenceJson);
  } catch {
    return out;
  }
  const obj = Array.isArray(parsed) ? parsed[0] : parsed;
  if (!obj || typeof obj !== "object") return out;
  const entries = Object.entries(obj as Record<string, unknown>);
  for (const field of fields) {
    const wanted = [field.key, ...(field.aliases ?? [])].map((k) => k.toLowerCase());
    const hit = entries.find(([k]) => wanted.includes(k.toLowerCase()));
    if (hit && hit[1] !== null && hit[1] !== undefined && hit[1] !== "") {
      out[field.key] = String(hit[1]);
    }
  }
  return out;
}

// The two renewal-bearing items (never BRK-TRN — internal training has no
// renewal cycle); the value is the noun used in the reminder appointment title.
const RENEWAL_NOUNS: Record<string, string> = {
  "BRK-INS": "E&O insurance",
  "BRK-LIC": "License",
};

/** The recorded expiration date (`YYYY-MM-DD`) for a renewal-bearing row,
 *  parsed from its stored evidence; null when absent/unparseable. */
export function renewalExpirationFor(row: BrokerCertificationDto): string | null {
  if (!RENEWAL_NOUNS[row.code]) return null;
  const raw = parseEvidence(row.evidenceJson, evidenceFieldsFor(row.code)).expirationDate;
  const match = raw ? /^(\d{4}-\d{2}-\d{2})/.exec(raw.trim()) : null;
  return match ? match[1] : null;
}

/** The day (`YYYY-MM-DD`) to place the renewal-call reminder on:
 *  `max(expiration − 60 days, tomorrow)` — never a past date. */
/**
 * The day to put the renewal reminder on: 60 days before the recorded
 * expiration, clamped so it is never today or earlier.
 *
 * 🔴 ONLY THE CLAMP ARM WAS EVER WRONG, and that is why it survived. `target`
 * is `Date.UTC(...)` over the expiration's own digits — zone-independent, the
 * same answer everywhere. `tomorrow` was `Date.UTC(now.getFullYear(),
 * now.getMonth(), now.getDate()) + DAY`, and those are LOCAL-TIME getters, so
 * it read the BROWSER's today. The two only diverge when the clamp binds —
 * an expiration inside 60 days, or already past — so a specialist in London
 * before 08:00 local got a reminder a day off the one every other surface
 * would have named, and only on the rows that were already urgent.
 *
 * It was self-consistent (local getters in, local day out), which is exactly
 * why no test and no ratchet could see it.
 *
 * `tz` is position 2 so the four existing positional `now` call sites become
 * TYPE ERRORS rather than silently binding a Date to a string.
 */
export function renewalReminderDate(
  expiration: string,
  tz: string | null,
  now: Date = new Date(),
): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(expiration.trim());
  if (!m) return null;
  const DAY = 86_400_000;
  const target = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - 60 * DAY;
  // Today AS THE VIEWER SEES IT, read back through the same UTC-midnight
  // convention `target` uses so the `Math.max` compares like with like.
  const [ty, tm, td] = isoDateInTz(now, tz).split("-").map(Number);
  const tomorrow = Date.UTC(ty, tm - 1, td) + DAY;
  return new Date(Math.max(target, tomorrow)).toISOString().slice(0, 10);
}

export function BrokerCertificationsTab({
  brokerId,
  brokerName,
  canQualify,
}: {
  brokerId: string;
  brokerName?: string;
  canQualify: boolean;
}) {
  const { date, tz } = useDateFormatter();
  const toast = useToast();
  const [openCode, setOpenCode] = useState<string | null>(null);
  // Session-only guard against accidental duplicate reminders (double-clicks).
  const [remindedCodes, setRemindedCodes] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  );
  const createAppointment = useCreateAppointment();
  const users = useAssignableUsers();
  const resolveUser = (userId?: string | null) =>
    userId
      ? users.data?.find((u) => u.id === userId)?.displayName ?? userId.slice(0, 8)
      : null;

  const certifications = useQuery({
    queryKey: ["broker-certifications", brokerId],
    queryFn: () => brokersApi.listCertifications(brokerId),
  });

  const rows = certifications.data ?? [];
  const openRow = rows.find((r) => r.code === openCode) ?? null;

  // "Remind me" — an all-day appointment on MY calendar 60 days before the
  // recorded expiration (clamped to tomorrow when it's closer/past). Composes
  // the instants exactly like the calendar form's all-day path.
  const remindMe = (row: BrokerCertificationDto, expiration: string) => {
    const noun = RENEWAL_NOUNS[row.code];
    const day = renewalReminderDate(expiration, tz);
    const startAt = day ? wallClockToUtcIso(day, "00:00", tz) : null;
    const endAt = day ? wallClockToUtcIso(day, "23:59", tz) : null;
    if (!noun || !startAt || !endAt) return;
    createAppointment.mutate(
      {
        title: `Call ${brokerName || "broker"} — ${noun} renewal due ${formatCalendarDate(expiration)}`,
        startAt,
        endAt,
        isAllDay: true,
        relatedEntityType: "Broker",
        relatedEntityId: brokerId,
      },
      {
        onSuccess: () => {
          toast.success("Reminder added to your calendar.");
          setRemindedCodes((cur) => new Set(cur).add(row.code));
        },
        onError: (e) => toast.error(extractErrorMessage(e)),
      },
    );
  };

  return (
    <div className="space-y-space-4">
      {certifications.isLoading && (
        <div className="text-caption text-gray-mid italic py-space-4">
          Loading certifications…
        </div>
      )}
      {certifications.isError && (
        <div className="text-caption text-status-error py-space-4">
          {extractErrorMessage(certifications.error)}
        </div>
      )}

      {!certifications.isLoading && rows.length > 0 && (
        <section className="bg-white-pure border border-border-light rounded-lg">
          <ul className="divide-y divide-border-light" aria-label="Broker certifications">
            {rows.map((row) => (
              <li key={row.id} className="px-space-3 py-space-3">
                <button
                  type="button"
                  className="w-full bg-transparent border-0 p-0 m-0 text-left cursor-pointer"
                  onClick={() => setOpenCode(row.code)}
                  data-testid={`broker-cert-row-${row.code}`}
                >
                  <div className="flex items-start justify-between gap-space-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-space-2">
                        <Badge status={STATUS_BADGE[row.status]}>{row.status}</Badge>
                        <h4 className="font-semibold text-small m-0">{row.title}</h4>
                      </div>
                      {row.shortDescription && (
                        <p className="text-caption text-gray-mid mt-space-1 m-0">
                          {row.shortDescription}
                        </p>
                      )}
                      {row.verifiedAt && (
                        <p className="text-caption text-gray-mid mt-space-1 m-0">
                          Verified by{" "}
                          <span className="text-onyx">{resolveUser(row.verifiedByUserId)}</span>{" "}
                          on {date(row.verifiedAt)}
                        </p>
                      )}
                      {row.status === "Waived" && (
                        <p
                          className="text-caption text-gray-mid mt-space-1 m-0"
                          data-testid={`broker-cert-waived-${row.code}`}
                        >
                          Waived by{" "}
                          <span className="text-onyx">{resolveUser(row.waivedByUserId)}</span>
                          {row.waivedAt ? ` on ${date(row.waivedAt)}` : ""}
                          {row.waivedReason ? ` — ${row.waivedReason}` : ""}
                        </p>
                      )}
                    </div>
                    {row.reportDocumentId && (
                      <span
                        className="inline-flex shrink-0 items-center gap-space-1 text-caption text-gold-shadow"
                        data-testid={`broker-cert-filed-${row.code}`}
                      >
                        <FileCheck2 size={14} aria-hidden /> Report filed
                      </span>
                    )}
                  </div>
                </button>
                {row.reportDocumentId && (
                  <Link
                    to={`/brokers/${brokerId}?tab=documents`}
                    className="text-caption text-gold-shadow hover:underline"
                  >
                    View in Documents →
                  </Link>
                )}
                {RENEWAL_NOUNS[row.code] && (
                  <div className="mt-space-2 flex flex-wrap items-center gap-space-3">
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={
                        !renewalExpirationFor(row) ||
                        remindedCodes.has(row.code) ||
                        createAppointment.isPending
                      }
                      title={
                        !renewalExpirationFor(row)
                          ? "Record an expiration date to set a reminder."
                          : undefined
                      }
                      onClick={() => {
                        const expiration = renewalExpirationFor(row);
                        if (expiration) remindMe(row, expiration);
                      }}
                      data-testid={`broker-cert-remind-${row.code}`}
                    >
                      <BellRing size={14} aria-hidden />{" "}
                      {remindedCodes.has(row.code) ? "Reminder added" : "Remind me"}
                    </Button>
                    {row.lastAutoRenewalRequestSentAtUtc && (
                      <span
                        className="text-caption text-gray-mid"
                        data-testid={`broker-cert-auto-sent-${row.code}`}
                      >
                        Renewal request auto-sent {date(row.lastAutoRenewalRequestSentAtUtc)}
                      </span>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {openRow && (
        <CertificationDrawer
          brokerId={brokerId}
          row={openRow}
          canQualify={canQualify}
          onClose={() => setOpenCode(null)}
        />
      )}
    </div>
  );
}

/* ---------------- Per-item drawer ---------------- */

function CertificationDrawer({
  brokerId,
  row,
  canQualify,
  onClose,
}: {
  brokerId: string;
  row: BrokerCertificationDto;
  canQualify: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const fields = evidenceFieldsFor(row.code);
  const [evidence, setEvidence] = useState<Record<string, string>>(() =>
    parseEvidence(row.evidenceJson, fields),
  );
  const [notes, setNotes] = useState(row.notes ?? "");
  const [confirmingReopen, setConfirmingReopen] = useState(false);
  const [waiving, setWaiving] = useState(false);
  const [waiveReason, setWaiveReason] = useState("");
  const [confirmingUnwaive, setConfirmingUnwaive] = useState(false);

  const refresh = () =>
    void qc.invalidateQueries({ queryKey: ["broker-certifications", brokerId] });

  const buildEvidenceJson = () => {
    const populated = Object.fromEntries(
      fields
        .map((f) => [f.key, (evidence[f.key] ?? "").trim()] as const)
        .filter(([, v]) => v !== ""),
    );
    return Object.keys(populated).length > 0 ? JSON.stringify(populated) : null;
  };

  // Required evidence must be filled before a certification can be recorded.
  const missingRequired = useMemo(
    () => fields.filter((f) => f.required && !(evidence[f.key] ?? "").trim()),
    [fields, evidence],
  );

  const record = useSubmittingMutation(
    () =>
      brokersApi.recordCertification(brokerId, row.code, {
        evidenceJson: buildEvidenceJson(),
        notes: notes.trim() || null,
      }),
    {
      onSuccess: () => {
        toast.success("Certification recorded.");
        refresh();
      },
      onError: (e) => toast.error(extractErrorMessage(e)),
    },
  );

  const verify = useSubmittingMutation(
    () => brokersApi.verifyCertification(brokerId, row.code, notes.trim() || null),
    {
      onSuccess: () => {
        toast.success("Certification verified.");
        refresh();
      },
      onError: (e) => toast.error(extractErrorMessage(e)),
    },
  );

  const reopen = useSubmittingMutation(
    () => brokersApi.reopenCertification(brokerId, row.code),
    {
      onSuccess: () => {
        toast.info("Certification reopened.");
        setConfirmingReopen(false);
        refresh();
      },
      onError: (e) => toast.error(extractErrorMessage(e)),
    },
  );

  const waive = useSubmittingMutation(
    () => brokersApi.waiveCertification(brokerId, row.code, waiveReason.trim()),
    {
      onSuccess: () => {
        toast.success("Certification waived.");
        setWaiving(false);
        setWaiveReason("");
        void qc.invalidateQueries({ queryKey: ["broker-certifications", brokerId] });
      },
      onError: (e) => toast.error(extractErrorMessage(e)),
    },
  );

  const unwaive = useSubmittingMutation(
    () => brokersApi.unwaiveCertification(brokerId, row.code),
    {
      onSuccess: () => {
        toast.info("Waiver removed.");
        setConfirmingUnwaive(false);
        void qc.invalidateQueries({ queryKey: ["broker-certifications", brokerId] });
      },
      onError: (e) => toast.error(extractErrorMessage(e)),
    },
  );

  return (
    <>
    <ResourceDrawer
      open
      onClose={onClose}
      // Don't dismiss on a scrim / outside click — an accidental click
      // shouldn't discard the evidence being entered.
      dismissOnBackdropClick={false}
      title={row.title}
      subtitle={`${row.code} — ${row.status}`}
      testId="broker-cert-drawer"
      footer={
        canQualify ? (
          <>
            {row.status !== "Pending" &&
              row.status !== "Waived" &&
              (confirmingReopen ? (
                <>
                  <span className="text-caption text-gray-mid">
                    Reopen to Pending? Evidence and stamps are retained.
                  </span>
                  <Button variant="secondary" size="sm" onClick={() => setConfirmingReopen(false)}>
                    Cancel
                  </Button>
                  <SubmitButton
                    pending={reopen.pending}
                    pendingLabel="Reopening…"
                    variant="destructive"
                    size="sm"
                    onClick={() => void reopen.submit()}
                    data-testid="broker-cert-reopen-confirm"
                  >
                    Confirm reopen
                  </SubmitButton>
                </>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setConfirmingReopen(true)}
                  data-testid="broker-cert-reopen"
                >
                  Reopen
                </Button>
              ))}
            {/* Un-waive returns a Waived item to Pending; a waived row shows no
                Record/Reopen (they'd leave stale waiver stamps). */}
            {row.status === "Waived" &&
              row.canUnwaive &&
              (confirmingUnwaive ? (
                <>
                  <span className="text-caption text-gray-mid">
                    Remove this waiver? The item returns to Pending.
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setConfirmingUnwaive(false)}
                  >
                    Cancel
                  </Button>
                  <SubmitButton
                    pending={unwaive.pending}
                    pendingLabel="Removing…"
                    variant="destructive"
                    size="sm"
                    onClick={() => void unwaive.submit()}
                    data-testid="broker-cert-unwaive-confirm"
                  >
                    Confirm un-waive
                  </SubmitButton>
                </>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setConfirmingUnwaive(true)}
                  data-testid="broker-cert-unwaive"
                >
                  Un-waive…
                </Button>
              ))}
            {row.status !== "Verified" && row.status !== "Waived" && (
              <SubmitButton
                pending={record.pending}
                pendingLabel="Recording…"
                variant={row.status === "Recorded" ? "secondary" : "primary"}
                disabled={missingRequired.length > 0}
                title={
                  missingRequired.length > 0
                    ? `Required: ${missingRequired.map((f) => f.label).join(", ")}`
                    : undefined
                }
                onClick={() => void record.submit()}
                data-testid="broker-cert-record"
              >
                {row.status === "Recorded" ? "Update evidence" : "Record"}
              </SubmitButton>
            )}
            {row.status === "Recorded" && (
              <SubmitButton
                pending={verify.pending}
                pendingLabel="Verifying…"
                onClick={() => void verify.submit()}
                data-testid="broker-cert-verify"
              >
                Verify
              </SubmitButton>
            )}
            {row.status === "Pending" && row.canWaive && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setWaiving(true)}
                data-testid="broker-cert-waive"
              >
                Waive…
              </Button>
            )}
          </>
        ) : undefined
      }
    >
      <div className="space-y-space-4">
        <p className="text-small whitespace-pre-wrap m-0">{row.description}</p>
        {row.status === "Waived" && (
          <div data-testid="broker-cert-waived-detail">
            <h4 className="text-caption font-semibold uppercase tracking-wide text-gold-shadow mb-space-1">
              Waiver reason
            </h4>
            <p className="text-small whitespace-pre-wrap m-0">
              {row.waivedReason || "No reason on file."}
            </p>
          </div>
        )}
        {row.requiredEvidence && (
          <div>
            <h4 className="text-caption font-semibold uppercase tracking-wide text-gold-shadow mb-space-1">
              Required evidence
            </h4>
            <p className="text-small whitespace-pre-wrap m-0">{row.requiredEvidence}</p>
          </div>
        )}

        <div>
          <h4 className="text-caption font-semibold uppercase tracking-wide text-gold-shadow mb-space-2">
            Evidence
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
            {fields.map((f) => (
              <FormField key={f.key} label={f.label} required={f.required}>
                <Input
                  type={f.type ?? "text"}
                  aria-label={f.label}
                  value={evidence[f.key] ?? ""}
                  disabled={!canQualify || row.status === "Verified"}
                  onChange={(e) =>
                    setEvidence((cur) => ({ ...cur, [f.key]: e.target.value }))
                  }
                />
              </FormField>
            ))}
          </div>
          {canQualify && row.status !== "Verified" && missingRequired.length > 0 && (
            <p className="text-caption text-status-error mt-space-2 m-0" role="alert">
              Required to record: {missingRequired.map((f) => f.label).join(", ")}.
            </p>
          )}
          <FormField label="Notes" className="mt-space-3">
            <Textarea
              rows={3}
              value={notes}
              disabled={!canQualify}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Context for the audit trail."
            />
          </FormField>
        </div>

        <CertificationReportsSection brokerId={brokerId} row={row} canQualify={canQualify} />
      </div>
    </ResourceDrawer>

    {waiving && (
      <Modal
        open
        onClose={() => {
          if (waive.pending) return;
          setWaiving(false);
        }}
        dismissOnBackdropClick={false}
        title="Waive certification"
        description={`${row.code} — ${row.title}`}
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setWaiving(false)}
              disabled={waive.pending}
            >
              Cancel
            </Button>
            <SubmitButton
              pending={waive.pending}
              pendingLabel="Waiving…"
              variant="destructive"
              disabled={!waiveReason.trim()}
              onClick={() => void waive.submit()}
              data-testid="broker-cert-waive-confirm"
            >
              Waive certification
            </SubmitButton>
          </>
        }
      >
        <p className="text-small text-gray-dark m-0 mb-space-3">
          Waiving marks this item satisfied without recorded evidence. Provide a
          reason for the audit trail; the item can be un-waived later.
        </p>
        <FormField label="Waiver reason" required>
          <Textarea
            rows={4}
            maxLength={4000}
            aria-label="Waiver reason"
            value={waiveReason}
            onChange={(e) => setWaiveReason(e.target.value)}
            placeholder="Why is this certification being waived?"
            data-testid="broker-cert-waive-reason"
          />
        </FormField>
      </Modal>
    )}
    </>
  );
}

/* ---------------- Reports (generate + file) ---------------- */

function CertificationReportsSection({
  brokerId,
  row,
  canQualify,
}: {
  brokerId: string;
  row: BrokerCertificationDto;
  canQualify: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { dateTime } = useDateFormatter();
  const [submissionId, setSubmissionId] = useState("");

  // Hide the whole section for an admin-added catalog item with no seeded
  // template — never a dead button.
  const templateId = row.reportTemplateId ?? null;

  const submissions = useQuery({
    queryKey: ["report-submissions", "mine"],
    queryFn: () => reportSubmissionsApi.mine({ page: 1, pageSize: 50 }),
    enabled: !!templateId && canQualify,
  });

  // The mine list has no broker filter on its DTO — filter to this item's
  // template with a generated artifact; the server's file-report endpoint
  // still enforces that the submission belongs to this broker.
  const candidates = useMemo(
    () =>
      (submissions.data?.data ?? []).filter(
        (s) => s.formTemplateId === templateId && !!s.generatedReportId,
      ),
    [submissions.data, templateId],
  );

  const file = useSubmittingMutation(
    () => brokersApi.fileCertificationReport(brokerId, row.code, submissionId),
    {
      onSuccess: () => {
        toast.success("Certification report filed.");
        void qc.invalidateQueries({ queryKey: ["broker-certifications", brokerId] });
      },
      onError: (e) => toast.error(extractErrorMessage(e)),
    },
  );

  if (!templateId) return null;

  return (
    <div>
      <h4 className="text-caption font-semibold uppercase tracking-wide text-gold-shadow mb-space-2">
        Reports
      </h4>
      <a
        href={`/reports/generate?templateId=${templateId}&brokerId=${brokerId}`}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonVariants({ variant: "secondary", size: "sm" })}
        data-testid="broker-cert-generate-report"
      >
        <ExternalLink size={14} aria-hidden /> Generate report
      </a>

      {canQualify && (
        <div className="mt-space-3">
          <FormField label="File generated report">
            <select
              className="w-full border border-border-light rounded-md p-space-2 text-small bg-white-pure"
              value={submissionId}
              onChange={(e) => setSubmissionId(e.target.value)}
              aria-label="Generated report submission"
              data-testid="broker-cert-submission-picker"
            >
              <option value="">
                {submissions.isLoading
                  ? "Loading your reports…"
                  : candidates.length === 0
                    ? "No generated reports for this template yet"
                    : "Select a generated report…"}
              </option>
              {candidates.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.formTemplateName} — {dateTime(s.createdAt)}
                  {s.preparedFor ? ` (${s.preparedFor})` : ""}
                </option>
              ))}
            </select>
          </FormField>
          <SubmitButton
            className="mt-space-2"
            size="sm"
            pending={file.pending}
            pendingLabel="Filing…"
            disabled={!submissionId}
            onClick={() => void file.submit()}
            data-testid="broker-cert-file-report"
          >
            File report
          </SubmitButton>
          <p className="text-caption text-gray-mid mt-space-1 m-0">
            Filing copies the rendered report into the broker&apos;s
            &ldquo;Licenses &amp; Certifications&rdquo; documents folder.
          </p>
        </div>
      )}
    </div>
  );
}
